import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { useTheme } from "styled-components";

import CenteredModal from "../../CenteredModal";
import DeleteIcon from "../../../../assets/components/DeleteIcon";
import Switch from "../../Switch";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import TypeAheadResultRow from "../../Calendar/TypeAheadResultRow";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";

import { ActorGroupDetail, ActorGroupEntry } from "../../../../store/model/actorGroupModel";

import { prepareAvailabilityDates } from "../../../../utility/search";

import { actorGroupSearchInFlightSelector, actorGroupSearchResultSelector, isSavingGroupSelector, saveCompleteSelector } from "../../../../store/selectors/actorGroupSelector";
import { clearSaveComplete, createActorGroup, searchActorGroups, updateActorGroup } from "../../../../store/actions/actorGroupActions";
import { roleSelectionSelector } from "../../../../store/selectors/searchSelectors";
import LoadingSpinner from "../../../../assets/components/LoadingSpinner";
import SearchIcon from "../../../../assets/components/SearchIcon";
import Client from "../../../../model/Client";
import ClientSearchDialog from "./ClientSearchDialog";




interface SaveRoleProps {
    show: boolean;
    defaultIndex?: number;
    currentActorGroup?: ActorGroupDetail | null;
    handleModalClose: () => void;
}


const SaveRoleDialog: React.FC<SaveRoleProps> = ({show, handleModalClose, defaultIndex = 0, currentActorGroup = null}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isSaving = useAppSelector(isSavingGroupSelector);       
    const roleSelections = useAppSelector((state: RootState) => roleSelectionSelector(state)); 
    const roleSearchResults = useAppSelector(actorGroupSearchResultSelector);
    const roleSearchIsLoading = useAppSelector(
        actorGroupSearchInFlightSelector
    );

    const [projectSearchText, setProjectSearchText] = useState<string>("");    
    const [saveRoleViewIndex, SetSaveRoleViewIndex] = useState<number>(defaultIndex);
    const [selectedProject, setSelectedProject] = useState<ActorGroupDetail | null>(currentActorGroup);
    const [projectDates, setProjectDates] = useState<(Date | null)[]>(currentActorGroup && currentActorGroup.bookingDates ? currentActorGroup.bookingDates : []);
    const [newProjectName, setNewProjectName] = useState<string>("");
    const [client, setClient] = useState<Client | null>(currentActorGroup?.client ?? null)
    const [invoiceClient, setInvoiceClient] = useState<Client | null>(currentActorGroup?.invoiceClient ?? null)
    const [showClientSearch, setShowClientSearch] = useState<boolean>(false);
    const [showInvoiceClientSearch, setShowInvoiceClientSearch] = useState<boolean>(false);


    const divider = "1px solid #dadada";

    const handleRoleSearch = useCallback(
        (searchTerm: string) => {
            dispatch(searchActorGroups({ name: searchTerm }));
        },
        [dispatch]
    );

    const handleRoleSearchInputChange = (
        text: string,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSelectedProject(null);
        setProjectDates([]);
        setProjectSearchText(text);
    };

    const handleSave = () => {
        if (roleSelections && roleSelections.length > 0) {
            dispatch(
                createActorGroup({                   
                        actorGroupName: newProjectName,
                        entries: roleSelections.map((actor) => {
                            var imageId = actor.currentImageId;                            
                            if((imageId === null || imageId === undefined) && (actor.primaryImageId !== null && actor.primaryImageId !== undefined)) {
                                imageId = actor.primaryImageId;                                
                            } else if((imageId === null || imageId === undefined) && (actor.primaryImageId === null && actor.primaryImageId === undefined)) {
                                imageId = -1;
                            }                       

                            return {
                                actorId: parseInt((actor.actorId + "").replace(/1000000\d/g, ''), 10),
                                imageId: imageId,
                                actorGroupId: null,
                                actorGroupEntryId: null                           
                            } as ActorGroupEntry
                        }),                
                        bookingDates: prepareAvailabilityDates(projectDates),
                        clientId: client?.clientId,
                        invoiceClientId: invoiceClient?.clientId

                    }
                )
            );
        }
    };

    const handleUpdate = () => {
        if (selectedProject) {
            dispatch(
                updateActorGroup({
                    actorGroupId: selectedProject.actorGroupId,
                    actorGroupName: selectedProject.actorGroupName,
                    entries: roleSelections.map((actor) => {
                        var imageId = actor.currentImageId;                        
                        if((imageId === null || imageId === undefined || imageId === -1) && (actor.primaryImageId !== null && actor.primaryImageId !== undefined)) {
                            imageId = actor.primaryImageId;                                
                        } else if((imageId === null || imageId === undefined) && (actor.primaryImageId === null && actor.primaryImageId === undefined)){
                            imageId = -1;
                        }                       

                        return {
                            actorId: parseInt((actor.actorId + "").replace(/1000000\d/g, ''), 10),
                            imageId: imageId,
                            actorGroupId: null,
                            actorGroupEntryId: null                           
                        } as ActorGroupEntry
                    }), 
                    bookingDates: prepareAvailabilityDates(projectDates),
                    clientId: client?.clientId,
                    invoiceClientId: invoiceClient?.clientId
                })
            );
        }
    };    

    const updateDate = (i: number, date: Date | null) => {
        const dates = [...projectDates];
        dates[i] = date
        setProjectDates(dates);
    }
    
    const removeDate = (index: number) => {
        var temp = [...projectDates]
        temp.splice(index, 1)
        setProjectDates(temp);
    }

    const addDate = () => {
        setProjectDates([...projectDates, new Date()]);
    }

    const handleClientSearchResult = (client: Client) => {
        if(showClientSearch){            
            setClient(client);
        }
        if(showInvoiceClientSearch){            
            setInvoiceClient(client);
        }
        handleCloseClientSearch();
    }

    const handleCloseClientSearch = () => {
        setShowClientSearch(false);
        setShowInvoiceClientSearch(false);
    }
       

    return <CenteredModal
            title="Create Role"
            show={show}
            onHide={() => handleModalClose()}
            vCenter={true}
        >
            <div style={{ padding: "1rem" }}>
                {!isSaving && !showClientSearch && !showInvoiceClientSearch && (
                    <>
                        <Switch
                            options={["New Role", "Existing Role"]}
                            selection={saveRoleViewIndex}
                            handleSwitchClick={(selection) => {
                                if(selection === 0){
                                    setNewProjectName("");
                                    setProjectDates([]);
                                    setClient(null);
                                    setInvoiceClient(null);
                                }
                                else {                                    
                                    setSelectedProject(currentActorGroup);
                                    setProjectDates([...(currentActorGroup?.bookingDates ?? [])]);
                                    setClient(currentActorGroup?.client ?? null);
                                    setInvoiceClient(currentActorGroup?.invoiceClient ?? null);
                                }
                                SetSaveRoleViewIndex(selection);
                            }}
                        />
                        {saveRoleViewIndex === 0 && <>
                            <Row>
                                <Col>
                                    <h5>Add to new project</h5>
                                </Col>
                            </Row>
                            <Row style={{ alignItems: "center" }}>
                                <Col>
                                    <FloatingLabel label="Project Name">
                                        <Form.Control
                                            id="projectName"
                                            placeholder="text"
                                            value={newProjectName}
                                            onChange={(e) =>
                                                setNewProjectName(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </FloatingLabel>
                                </Col>                                    
                            </Row>
                            <Row className="mt-3 d-flex align-items-center">
                                <Col>
                                    <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
                                        <h5>Dates</h5>                                       
                                        <Chip onClick={(e) => addDate()} label="+" color="success" className="me-3"/>                                        
                                    </Box>
                                </Col>
                                <Col style={{ borderLeft: divider}}>
                                    <h5>Client</h5>                                 
                                </Col>                                  
                            </Row>
                            <Row>
                                <Col>
                                    { projectDates.length === 0 && <p>No dates set</p>}
                                    { projectDates.length > 0 && 
                                        projectDates.map((x, i) => {
                                            return <div className="p-2 d-flex align-items-center" key={i}>
                                            <DatePicker                    
                                                renderInput={(params) => <TextField {...params} />}                        
                                                value={projectDates[i]}                    
                                                inputFormat="dd/MM/yyyy"
                                                onChange={(newVal) => updateDate(i, newVal)}
                                                />
                                            <div className="m-3" onClick={(e) => removeDate(i)}>
                                                <DeleteIcon fill={theme.palette.error.main} 
                                                    style={{ width: "24px", height: "24px"}}
                                                    
                                                />
                                            </div>
                                        </div>
                                        })
                                    }
                                </Col>
                                <Col style={{ borderLeft: divider}}>
                                    <div className="p-2 d-flex align-items-center" key={0}>                                        
                                        <TextField 
                                            id="client-field"
                                            label="Client"                                                                                                                                     
                                            variant="outlined" 
                                            disabled={true}                                            
                                            value={client?.code &&  client?.name ? `${client.code} - ${client.name}`: ""}
                                            InputProps={{
                                                endAdornment: ( <InputAdornment position="start">
                                                    <IconButton onClick={() => setShowClientSearch(true)}><SearchIcon /></IconButton>
                                                  </InputAdornment> 
                                                )                                              
                                            }}                                                                                                                         
                                        />
                                    </div>
                                    <div className="p-2 d-flex align-items-center" key={1}>
                                        <TextField 
                                            id="invoiceClient-field"
                                            label="Bill To Client"                                                                                                                                     
                                            variant="outlined" 
                                            disabled={true}                                            
                                            value={invoiceClient?.code &&  invoiceClient?.name ? `${invoiceClient.code} - ${invoiceClient.name}`: ""}
                                            InputProps={{
                                                endAdornment: ( <InputAdornment position="start">
                                                    <IconButton onClick={() => setShowInvoiceClientSearch(true)}><SearchIcon /></IconButton>
                                                    </InputAdornment> 
                                                )                                              
                                            }}                                                                                                                         
                                        />                                        
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Button
                                        style={{ color: "white" }}
                                        onClick={(e) => handleSave()}
                                        disabled={
                                            isSaving ||
                                            newProjectName.length === 0
                                        }
                                        size="lg"
                                    >
                                        Save
                                    </Button>
                                </Col>
                            </Row>
                        </>
                        }
                        
                        {saveRoleViewIndex === 1 && <>
                            <Row>
                                <Col>
                                    <h5>Existing Role</h5>
                                </Col>
                            </Row>
                            <Row  style={{ alignItems: "center" }}>
                                <Col>
                                    <AsyncTypeahead
                                        id="existing-role-search"
                                        onSearch={handleRoleSearch}
                                        onInputChange={
                                            handleRoleSearchInputChange
                                        }
                                        onChange={(selectedRole: any) => {
                                            if(selectedRole && selectedRole.length > 0){
                                                setSelectedProject(
                                                    selectedRole[0]
                                                );
                                                
                                                const newDates = selectedRole[0].bookingDates;
                                                setProjectDates(newDates === null ? [] : newDates);
                                                                                    
                                            }                 
                                        }}
                                        isLoading={roleSearchIsLoading}
                                        options={roleSearchResults}
                                        inputProps={{
                                            value: projectSearchText,
                                            style: { padding: "1rem" },
                                        }}
                                        labelKey="actorGroupName"
                                        renderMenuItemChildren={(
                                            option: any
                                        ) => (
                                            <TypeAheadResultRow
                                                id={option.actorGroupId}
                                                name={option.actorGroupName}
                                            />
                                        )}
                                        selected={
                                            selectedProject
                                                ? [selectedProject as any]
                                                : []
                                        }
                                        placeholder="Search for project to assign actors to"
                                        useCache={false}
                                    />
                                </Col>
                            </Row>
                            <Row className="mt-3 d-flex align-items-center">
                                <Col>
                                    <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
                                        <h5>Dates</h5>                                       
                                        <Chip onClick={(e) => addDate()} label="+" color="success" className="me-3"/>                                        
                                    </Box>
                                </Col>
                                <Col style={{ borderLeft: divider}}>
                                    <h5>Client</h5>                                 
                                </Col>                                    
                            </Row>
                            <Row>
                                <Col>
                                    { projectDates.length === 0 && <p>No dates set</p>}
                                    { projectDates.length > 0 && 
                                        projectDates.map((x, i) => {
                                            return <div className="p-2 d-flex align-items-center" key={i}>
                                            <DatePicker                    
                                                renderInput={(params) => <TextField {...params} />}                        
                                                value={projectDates[i]}                    
                                                inputFormat="dd/MM/yyyy"
                                                onChange={(newVal) => updateDate(i, newVal)}
                                                />
                                            <div className="m-3" onClick={(e) => removeDate(i)}>
                                                <DeleteIcon fill={theme.palette.error.main} 
                                                    style={{ width: "24px", height: "24px"}}
                                                    
                                                />
                                            </div>
                                        </div>
                                        })
                                    }
                                </Col>
                                <Col style={{ borderLeft: divider}}>
                                    <div className="p-2 d-flex align-items-center" key={0}>                                        
                                        <TextField 
                                            id="client-field"
                                            label="Client"                                                                                                                                     
                                            variant="outlined" 
                                            disabled={true}                                            
                                            value={client?.code &&  client?.name ? `${client.code} - ${client.name}`: ""}
                                            InputProps={{
                                                endAdornment: ( <InputAdornment position="start">
                                                    <IconButton onClick={() => setShowClientSearch(true)}><SearchIcon /></IconButton>
                                                  </InputAdornment> 
                                                )                                              
                                            }}                                                                                                                         
                                        />
                                    </div>
                                    <div className="p-2 d-flex align-items-center" key={1}>
                                        <TextField 
                                            id="invoiceClient-field"
                                            label="Bill To Client"                                                                                                                                     
                                            variant="outlined" 
                                            disabled={true}                                            
                                            value={invoiceClient?.code &&  invoiceClient?.name ? `${invoiceClient.code} - ${invoiceClient.name}`: ""}
                                            InputProps={{
                                                endAdornment: ( <InputAdornment position="start">
                                                        <IconButton onClick={() => setShowInvoiceClientSearch(true)}><SearchIcon /></IconButton>
                                                    </InputAdornment> 
                                                )                                              
                                            }}                                                                                                                         
                                        />                                        
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Button
                                        style={{ color: "white" }}
                                        onClick={(e) => handleUpdate()}
                                        disabled={
                                            isSaving ||
                                            selectedProject === null
                                        }
                                        size="lg"
                                    >
                                        Save
                                    </Button>
                                </Col>
                            </Row>
                            </>
                            }
                            
                    </>
                )}

                {(showInvoiceClientSearch || showClientSearch) &&
                    <ClientSearchDialog handleClientSelection={handleClientSearchResult} handleModalClose={handleCloseClientSearch} />
                }

                {isSaving && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <LoadingSpinner style={{ width: "100px" }} />
                    </div>
                )}
            </div>
        </CenteredModal>
}

export default SaveRoleDialog;
